import React, { useState, useMemo, useEffect } from "react";
import { Card, Table, Row, Col, Form, Button, Dropdown, Badge, Modal, Spinner, Toast, ToastContainer, OverlayTrigger, Tooltip } from "react-bootstrap";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import Footer from "../layouts/Footer";
import { useNavigate } from 'react-router-dom';
import {
  fetchEntitesList,
  addEntite,
  updateEntite,
  deleteEntite
} from "../services/api";

// ============================================
// UTILITAIRES
// ============================================
const formatPhoneNumber = (numero) => {
  if (!numero) return numero;
  if (numero.startsWith('241') && numero.length === 11) {
    return `+241 ${numero.substring(3, 5)} ${numero.substring(5, 8)} ${numero.substring(8)}`;
  }
  return numero;
};

const getNotificationIcon = (type) => {
  switch (type) {
    case 'success': return 'bi-check-circle-fill';
    case 'error': return 'bi-exclamation-circle-fill';
    case 'warning': return 'bi-exclamation-triangle-fill';
    case 'info': return 'bi-info-circle-fill';
    default: return 'bi-bell-fill';
  }
};

const getNotificationBg = (type) => {
  switch (type) {
    case 'success': return 'success';
    case 'error': return 'danger';
    case 'warning': return 'warning';
    case 'info': return 'info';
    default: return 'primary';
  }
};

const getAlertIcon = (type) => {
  switch (type) {
    case 'success': return 'bi-check-circle';
    case 'danger': return 'bi-exclamation-triangle';
    case 'warning': return 'bi-exclamation-triangle';
    case 'info': return 'bi-info-circle';
    default: return 'bi-bell';
  }
};

export default function EntiteManagement() {
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('reg_date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedEntites, setSelectedEntites] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const [showAddEntiteModal, setShowAddEntiteModal] = useState(false);
  const [showEditEntiteModal, setShowEditEntiteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

  const [error, setError] = useState("");
  const [entites, setEntites] = useState([]);
  const [loadingEntites, setLoadingEntites] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);

  const [notifications, setNotifications] = useState([]);
  const [alertMessage, setAlertMessage] = useState({ show: false, message: '', type: 'info' });

  const navigate = useNavigate();

  const handleGoToPricing = (entite) => {
    setShowDetailsModal(false);
    sessionStorage.setItem('temp_business_name', entite.business_name);
    navigate(`/apps/pricing?entite_id=${entite.id}`);
  };

  const [newEntite, setNewEntite] = useState({
    business_name: '', business_email: '', phone: '',
    business_country: '', business_city: '', acc_type: 'business'
  });

  const [selectedEntite, setSelectedEntite] = useState(null);
  const [editEntite, setEditEntite] = useState({
    business_name: '', business_email: '', phone: '',
    business_country: '', business_city: '', acc_type: 'business'
  });

  const [entiteToDelete, setEntiteToDelete] = useState(null);

  // ============================================
  // NOTIFICATIONS
  // ============================================
  const showNotification = (message, type = 'info', duration = 5000) => {
    const id = Date.now();
    setNotifications(prev => [{ id, message, type, show: true }, ...prev]);
    setTimeout(() => hideNotification(id), duration);
  };

  const hideNotification = (id) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
  };

  const showAlert = (message, type = 'warning', duration = 5000) => {
    setAlertMessage({ show: true, message, type });
    if (duration > 0) setTimeout(() => hideAlert(), duration);
  };

  const hideAlert = () => {
    setAlertMessage({ show: false, message: '', type: 'info' });
  };

  // ============================================
  // CHARGEMENT
  // ============================================
  useEffect(() => {
    loadEntites();
  }, []);

  const loadEntites = async (showRefreshSpinner = false) => {
    try {
      if (showRefreshSpinner) setRefreshing(true);
      else setLoadingEntites(true);
      setError("");

      const token = localStorage.getItem('authToken');
      if (!token) {
        setError("Token d'authentification non trouvé");
        showAlert("Token d'authentification non trouvé. Veuillez vous reconnecter.", 'danger');
        return;
      }

      const entitesData = await fetchEntitesList(token);

      if (!Array.isArray(entitesData)) {
        setError("Format de données invalide reçu du serveur");
        showAlert("Format de données invalide reçu du serveur", 'danger');
        return;
      }

      const formattedEntites = entitesData.map(entite => ({
        id: entite.business_id,
        business_id: entite.business_id || '',
        firstname: entite.firstname || '',
        lastname: entite.lastname || '',
        business_name: entite.business_name || '',
        business_email: entite.business_email || '',
        business_country: entite.business_country || '',
        business_city: entite.business_city || '',
        reg_date: entite.reg_date || '',
        country_code: entite.country_code || '',
        acc_type: entite.acc_type || 'business',
        kyc: entite.kyc || 'KO',
        sender: entite.sender || '',
        phone: entite.phone || '',
        has_mfa: entite.has_mfa || 'NO',
        updated_at: entite.updated_at,
        created_at: entite.created_at
      }));

      setEntites(formattedEntites);
      if (showRefreshSpinner) {
        showNotification('Liste des entités actualisée', 'success', 3000);
      }
    } catch (error) {
      console.error('❌ Erreur lors du chargement des entités:', error);
      const errorMessage = error.message || "Erreur lors du chargement des entités";
      setError(errorMessage);
      showAlert(errorMessage, 'danger');
    } finally {
      setLoadingEntites(false);
      setRefreshing(false);
    }
  };

  // ============================================
  // CRÉATION
  // ============================================
  const handleSaveEntite = async () => {
    if (!newEntite.business_name.trim() || !newEntite.business_email.trim()) {
      showAlert("Veuillez remplir les champs obligatoires", 'warning');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEntite.business_email)) {
      showAlert("Veuillez saisir une adresse email valide", 'warning');
      return;
    }

    setCreateLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Token d\'authentification manquant');

      const entiteData = {
        business_name: newEntite.business_name.trim(),
        business_email: newEntite.business_email.trim(),
        phone: newEntite.phone.trim() || '',
        business_country: newEntite.business_country.trim() || '',
        business_city: newEntite.business_city.trim() || '',
        acc_type: newEntite.acc_type
      };

      await addEntite(token, entiteData);

      setNewEntite({
        business_name: '', business_email: '', phone: '',
        business_country: '', business_city: '', acc_type: 'business'
      });
      setShowAddEntiteModal(false);
      await loadEntites();
      showNotification(`Entité "${entiteData.business_name}" créée avec succès`, 'success', 5000);
    } catch (err) {
      console.error('❌ Erreur création:', err);
      if (err.message.includes('409')) showAlert('Une entité avec cet email existe déjà', 'warning');
      else if (err.message.includes('400')) showAlert('Données invalides', 'warning');
      else if (err.message.includes('401')) showAlert('Session expirée. Reconnectez-vous.', 'danger');
      else showAlert(`Erreur lors de la création: ${err.message}`, 'error');
    } finally {
      setCreateLoading(false);
    }
  };

  // ============================================
  // ÉDITION
  // ============================================
  const handleEditEntite = (entite) => {
    setSelectedEntite(entite);
    setEditEntite({
      business_name: entite.business_name || '',
      business_email: entite.business_email || '',
      phone: entite.phone || '',
      business_country: entite.business_country || '',
      business_city: entite.business_city || '',
      acc_type: entite.acc_type || 'business'
    });
    setShowEditEntiteModal(true);
  };

  const handleUpdateEntite = async () => {
    if (!selectedEntite) return;
    if (!editEntite.business_name.trim() || !editEntite.business_email.trim()) {
      showAlert("Veuillez remplir les champs obligatoires", 'warning');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editEntite.business_email)) {
      showAlert("Veuillez saisir une adresse email valide", 'warning');
      return;
    }

    setUpdateLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Token d\'authentification manquant');

      const entiteData = {
        business_name: editEntite.business_name.trim(),
        business_email: editEntite.business_email.trim(),
        phone: editEntite.phone.trim() || null,
        business_country: editEntite.business_country.trim() || null,
        business_city: editEntite.business_city.trim() || null,
        acc_type: editEntite.acc_type,
        country_code: 'GA'
      };

      await updateEntite(token, selectedEntite.business_id, entiteData);
      setShowEditEntiteModal(false);
      setSelectedEntite(null);
      await loadEntites();
      showNotification(`Entité "${entiteData.business_name}" mise à jour`, 'success', 5000);
    } catch (err) {
      console.error('❌ Erreur mise à jour:', err);
      let errorMessage = `Erreur: ${err.message}`;
      if (err.message.includes('500')) errorMessage = 'Erreur serveur. Réessayez.';
      else if (err.message.includes('400')) errorMessage = 'Données invalides.';
      else if (err.message.includes('404')) errorMessage = 'Entité non trouvée.';
      else if (err.message.includes('401')) {
        errorMessage = 'Session expirée. Reconnectez-vous.';
        setTimeout(() => { window.location.href = '/login'; }, 2000);
      }
      showAlert(errorMessage, 'error');
    } finally {
      setUpdateLoading(false);
    }
  };

  // ============================================
  // SUPPRESSION
  // ============================================
  const handleShowDetails = (entite) => {
    setSelectedEntite(entite);
    setShowDetailsModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!entiteToDelete) return;
    setDeleteLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Token d\'authentification manquant');

      const response = await deleteEntite(token, entiteToDelete.business_id);

      if (response && (response.code === 200 || response.success)) {
        setEntites(prev => prev.filter(e => e.business_id !== entiteToDelete.business_id));
        showNotification(`Entité "${entiteToDelete.business_name}" supprimée`, 'success', 5000);
      } else {
        throw new Error(response?.message || 'Erreur suppression');
      }
    } catch (err) {
      console.error('❌ Erreur suppression:', err);
      if (err.message.includes('404')) {
        setEntites(prev => prev.filter(e => e.business_id !== entiteToDelete.business_id));
        showNotification(`Entité supprimée`, 'success', 5000);
      } else if (err.message.includes('401')) {
        showAlert('Session expirée. Reconnectez-vous.', 'danger');
      } else {
        showAlert(`Erreur: ${err.message}`, 'error');
      }
    } finally {
      setDeleteLoading(false);
      setShowDeleteModal(false);
      setEntiteToDelete(null);
    }
  };

  const handleConfirmBulkDelete = async () => {
    if (selectedEntites.length === 0) return;
    setBulkDeleteLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      if (!token) throw new Error('Token d\'authentification manquant');

      const deletePromises = selectedEntites.map(businessId => deleteEntite(token, businessId));
      const results = await Promise.allSettled(deletePromises);

      const successfulIds = [];
      results.forEach((result, index) => {
        if (result.status === 'fulfilled' &&
            (result.value?.code === 200 || result.value?.success === true)) {
          successfulIds.push(selectedEntites[index]);
        }
      });

      if (successfulIds.length > 0) {
        setEntites(prev => prev.filter(e => !successfulIds.includes(e.business_id)));
        showNotification(`${successfulIds.length} entité(s) supprimée(s)`, 'success', 5000);
      }
      setSelectedEntites([]);
    } catch (err) {
      console.error('❌ Erreur suppression multiple:', err);
      showAlert(`Erreur: ${err.message}`, 'error');
    } finally {
      setBulkDeleteLoading(false);
      setShowBulkDeleteModal(false);
    }
  };

  const handleDeleteEntite = (entite) => {
    setEntiteToDelete(entite);
    setShowDeleteModal(true);
  };

  const handleBulkDelete = () => {
    if (selectedEntites.length === 0) {
      showAlert("Sélectionnez au moins une entité", 'warning');
      return;
    }
    setShowBulkDeleteModal(true);
  };

  const handleAddEntite = () => setShowAddEntiteModal(true);

  // ============================================
  // FILTRAGE ET TRI
  // ============================================
  const filteredAndSortedItems = useMemo(() => {
    let filtered = entites.filter(item => {
      const searchTerm = filterText.toLowerCase();
      if (!searchTerm.trim()) return true;
      return (
        (item.business_name && item.business_name.toLowerCase().includes(searchTerm)) ||
        (item.business_email && item.business_email.toLowerCase().includes(searchTerm)) ||
        (item.phone && item.phone.includes(filterText)) ||
        (item.business_country && item.business_country.toLowerCase().includes(searchTerm)) ||
        (item.business_city && item.business_city.toLowerCase().includes(searchTerm)) ||
        (item.acc_type && item.acc_type.toLowerCase().includes(searchTerm))
      );
    });

    filtered.sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      if (sortDirection === 'asc') return aValue > bValue ? 1 : -1;
      return aValue < bValue ? 1 : -1;
    });

    return filtered;
  }, [entites, filterText, sortField, sortDirection]);

  // ============================================
  // STATS
  // ============================================
  const stats = useMemo(() => {
    const total = entites.length;
    const actifs = entites.filter(e => e.kyc === 'OK').length;
    const inactifs = total - actifs;
    const admins = entites.filter(e => e.acc_type === 'admin').length;
    const businesses = entites.filter(e => e.acc_type === 'business').length;
    return { total, actifs, inactifs, admins, businesses };
  }, [entites]);

  // ============================================
  // PAGINATION
  // ============================================
  const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDirection('asc'); }
  };

  // ============================================
  // SÉLECTION
  // ============================================
  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedEntites(currentItems.map(e => e.business_id));
    else setSelectedEntites([]);
  };

  const handleSelectEntite = (businessId) => {
    setSelectedEntites(prev =>
      prev.includes(businessId)
        ? prev.filter(id => id !== businessId)
        : [...prev, businessId]
    );
  };

  const isAllSelected = currentItems.length > 0 && selectedEntites.length === currentItems.length;
  const isSomeSelected = selectedEntites.length > 0 && selectedEntites.length < currentItems.length;

  // ============================================
  // EXPORT
  // ============================================
  const handleExport = (format) => {
    setExportLoading(true);
    const dataToExport = selectedEntites.length > 0
      ? entites.filter(e => selectedEntites.includes(e.business_id))
      : filteredAndSortedItems;

    if (!dataToExport.length) {
      showAlert("Aucune donnée à exporter", 'warning', 4000);
      setExportLoading(false);
      return;
    }

    try {
      const enhancedData = dataToExport.map(entite => ({
        ...entite,
        statut: entite.kyc === "OK" ? "Validé" : "En attente",
        type_compte: entite.acc_type === "admin" ? "Administrateur" :
                    entite.acc_type === "business" ? "Entreprise" :
                    entite.acc_type === "system" ? "Système" : "Autre",
        nom_complet: entite.firstname && entite.lastname ? `${entite.firstname} ${entite.lastname}` : 'Non renseigné',
        numeroFormate: formatPhoneNumber(entite.phone)
      }));

      if (format === "csv") {
        const headers = ["ID","Nom Entreprise","Email","Téléphone","Pays","Ville","Type Compte","Statut KYC","Date Inscription"];
        const csvContent = [
          "\uFEFF" + headers.join(";"),
          ...enhancedData.map(e => [
            e.business_id, e.business_name, e.business_email, e.phone,
            e.business_country, e.business_city, e.type_compte, e.statut, e.reg_date
          ].map(v => `"${v || ''}"`).join(";"))
        ].join("\n");
        downloadFile(csvContent, "text/csv;charset=utf-8;", `export-entites-${Date.now()}.csv`);
      } else if (format === "excel") {
        const ws = XLSX.utils.json_to_sheet(enhancedData.map(e => ({
          ID: e.business_id,
          "Nom Entreprise": e.business_name,
          Email: e.business_email,
          Téléphone: e.phone,
          Pays: e.business_country,
          Ville: e.business_city,
          "Type Compte": e.type_compte,
          "Statut KYC": e.statut,
          "Date Inscription": e.reg_date
        })));
        ws['!cols'] = [
          { wch: 8 }, { wch: 25 }, { wch: 25 }, { wch: 15 },
          { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 18 }
        ];
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Entités");
        XLSX.writeFile(wb, `export-entites-${Date.now()}.xlsx`);
      } else if (format === "json") {
        downloadFile(JSON.stringify(enhancedData, null, 2), "application/json;charset=utf-8;", `export-entites-${Date.now()}.json`);
      } else if (format === "pdf") {
        generatePDF(enhancedData);
      }

      showNotification(`Export ${format.toUpperCase()} réussi`, 'success', 4000);
    } catch (error) {
      showAlert(`Erreur export: ${error.message}`, 'error', 6000);
    } finally {
      setExportLoading(false);
    }
  };

  const generatePDF = (data) => {
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 15;
      let yPosition = margin;

      const primaryColor = [44, 62, 120];
      const softGray = [245, 246, 248];
      const darkText = [40, 40, 40];
      const borderGray = [210, 214, 220];

      doc.setFillColor(...primaryColor);
      doc.rect(0, 0, pageWidth, 30, "F");

      doc.setTextColor(255, 255, 255);
      doc.setFontSize(18);
      doc.setFont(undefined, "bold");
      doc.text("LISTE DES ENTITÉS", pageWidth / 2, 12, { align: "center" });

      doc.setFontSize(9);
      doc.setFont(undefined, "normal");
      doc.text(`Généré le ${new Date().toLocaleDateString("fr-FR")} • ${data.length} entité(s)`, pageWidth / 2, 20, { align: "center" });

      yPosition = 40;

      const headers = ["ENTREPRISE", "EMAIL", "TÉLÉPHONE", "PAYS", "TYPE", "STATUT"];
      const columnWidths = [40, 50, 25, 25, 20, 20];
      const totalWidth = columnWidths.reduce((a, b) => a + b);

      const drawTableHeader = () => {
        doc.setFillColor(...primaryColor);
        doc.rect(margin, yPosition, totalWidth, 9, "F");
        let x = margin;
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(8);
        doc.setFont(undefined, "bold");
        headers.forEach((head, i) => {
          doc.text(head, x + 2, yPosition + 6);
          x += columnWidths[i];
        });
        yPosition += 9;
      };

      drawTableHeader();
      doc.setFontSize(7);
      doc.setFont(undefined, "normal");

      data.forEach((row, index) => {
        if (yPosition > 265) {
          doc.addPage();
          yPosition = 20;
          drawTableHeader();
        }
        if (index % 2 === 0) {
          doc.setFillColor(...softGray);
          doc.rect(margin, yPosition, totalWidth, 7, "F");
        }
        let x = margin;
        doc.setTextColor(...darkText);
        const rowData = [
          row.business_name || "-", row.business_email || "-",
          row.numeroFormate || row.phone || "-", row.business_country || "-",
          row.type_compte || "-", row.statut || "-"
        ];
        rowData.forEach((cell, i) => {
          let text = cell.toString();
          const limit = [25, 35, 12, 12, 10, 10][i];
          if (text.length > limit) text = text.substring(0, limit - 3) + "...";
          doc.text(text, x + 2, yPosition + 5);
          x += columnWidths[i];
        });
        yPosition += 7;
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setDrawColor(...borderGray);
        doc.line(margin, 283, pageWidth - margin, 283);
        doc.setFontSize(7);
        doc.setTextColor(110, 110, 110);
        doc.text(`Page ${i} / ${pageCount}`, pageWidth / 2, 289, { align: "center" });
      }

      doc.save(`entites-${new Date().toISOString().split("T")[0]}.pdf`);
    } catch (error) {
      console.error("❌ Erreur PDF:", error);
      throw new Error(`Erreur PDF: ${error.message}`);
    }
  };

  const downloadFile = (content, type, filename) => {
    const blob = new Blob([content], { type });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    setTimeout(() => URL.revokeObjectURL(link.href), 100);
  };

  const SortableHeader = ({ field, children }) => (
    <th className="en-sortable-header" onClick={() => handleSort(field)}>
      <span className="en-sortable-content">
        {children}
        {sortField === field && (
          <i className={`bi bi-caret-${sortDirection === 'asc' ? 'up' : 'down'}-fill en-sort-icon`}></i>
        )}
      </span>
    </th>
  );

  // ============================================
  // HELPERS UI
  // ============================================
  const getAccountTypeClass = (accType) => {
    switch (accType) {
      case 'admin': return 'en-type-admin';
      case 'business': return 'en-type-business';
      case 'system': return 'en-type-system';
      default: return 'en-type-default';
    }
  };

  const getAccountTypeLabel = (accType) => {
    switch (accType) {
      case 'admin': return 'Administrateur';
      case 'business': return 'Entreprise';
      case 'system': return 'Système';
      default: return accType;
    }
  };

  const getAccountTypeIcon = (accType) => {
    switch (accType) {
      case 'admin': return 'bi-shield-fill-check';
      case 'business': return 'bi-briefcase-fill';
      case 'system': return 'bi-gear-fill';
      default: return 'bi-building';
    }
  };

  const getCountryFlag = (country) => {
    if (!country) return '';
    const flags = { 'GA': '🇬🇦', 'SN': '🇸🇳', 'CG': '🇨🇬', 'CI': '🇨🇮', 'CM': '🇨🇲', 'FR': '🇫🇷' };
    return flags[country] || '';
  };

  const getCountryName = (country) => {
    if (!country) return '';
    const names = { 'GA': 'Gabon', 'SN': 'Sénégal', 'CG': 'Congo', 'CI': 'Côte d\'Ivoire', 'CM': 'Cameroun', 'FR': 'France' };
    return names[country] || country;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className="en-page main main-app p-3 p-lg-4">
      {/* ========== TOASTS ========== */}
      <ToastContainer position="top-end" className="p-3" style={{ zIndex: 9999 }}>
        {notifications.map((notification) => {
          const bgColor = getNotificationBg(notification.type);
          const icon = getNotificationIcon(notification.type);
          return (
            <Toast
              key={notification.id}
              show={notification.show}
              onClose={() => hideNotification(notification.id)}
              delay={8000}
              autohide
              bg={bgColor}
            >
              <Toast.Header className={`bg-${bgColor} text-white`}>
                <i className={`bi ${icon} me-2`}></i>
                <strong className="me-auto">
                  {notification.type === 'success' ? 'Succès' :
                   notification.type === 'error' ? 'Erreur' :
                   notification.type === 'warning' ? 'Attention' : 'Information'}
                </strong>
                <small>{new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</small>
              </Toast.Header>
              <Toast.Body className="text-white">{notification.message}</Toast.Body>
            </Toast>
          );
        })}
      </ToastContainer>

      {/* ========== ALERT ========== */}
      {alertMessage.show && (
        <div className={`en-alert en-alert-${alertMessage.type} mb-4`}>
          <i className={`bi ${getAlertIcon(alertMessage.type)}`}></i>
          <div className="flex-grow-1">{alertMessage.message}</div>
          <button className="en-alert-close" onClick={hideAlert}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
      )}

      {/* ========== HEADER ========== */}
      <div className="en-header mb-4">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="en-header-icon">
              <i className="bi bi-buildings-fill"></i>
            </div>
            <div>
              <h3 className="en-header-title mb-1">Gestion des entités</h3>
              <p className="en-header-subtitle mb-0">
                Administration des entreprises et comptes système
              </p>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2">
            {selectedEntites.length > 0 && (
              <Button className="en-btn-danger" onClick={handleBulkDelete}>
                <i className="bi bi-trash"></i>
                <span>Supprimer ({selectedEntites.length})</span>
              </Button>
            )}

            <Dropdown>
              <Dropdown.Toggle className="en-btn-secondary" disabled={exportLoading}>
                <i className={`bi ${exportLoading ? 'bi-arrow-repeat en-spin' : 'bi-download'}`}></i>
                <span>Exporter</span>
              </Dropdown.Toggle>
              <Dropdown.Menu className="en-dropdown-menu">
                <Dropdown.Item onClick={() => handleExport('csv')}>
                  <i className="bi bi-file-earmark-spreadsheet text-success me-2"></i>Export CSV
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleExport('excel')}>
                  <i className="bi bi-file-earmark-excel text-success me-2"></i>Export Excel
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleExport('pdf')}>
                  <i className="bi bi-file-earmark-pdf text-danger me-2"></i>Export PDF
                </Dropdown.Item>
                <Dropdown.Item onClick={() => handleExport('json')}>
                  <i className="bi bi-file-earmark-code text-warning me-2"></i>Export JSON
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>

            <Button className="en-btn-primary" onClick={() => loadEntites(true)} disabled={refreshing}>
              <i className={`bi ${refreshing ? 'bi-arrow-repeat en-spin' : 'bi-arrow-clockwise'}`}></i>
              <span>{refreshing ? 'Actualisation...' : 'Actualiser'}</span>
            </Button>
          </div>
        </div>
      </div>

      {/* ========== ERROR ========== */}
      {error && (
        <div className="en-alert en-alert-danger mb-4">
          <i className="bi bi-exclamation-triangle-fill"></i>
          <div className="flex-grow-1">{error}</div>
          <Button variant="outline-danger" size="sm" onClick={() => loadEntites(true)}>
            <i className="bi bi-arrow-clockwise me-1"></i>Réessayer
          </Button>
        </div>
      )}

      {/* ========== STATS ========== */}
      <Row className="g-3 mb-4">
        <Col xs={6} md={4} lg={3}>
          <div className="en-stat-card">
            <div className="en-stat-icon-wrapper en-stat-icon-blue">
              <i className="bi bi-buildings-fill"></i>
            </div>
            <div className="en-stat-content">
              <div className="en-stat-label">Total entités</div>
              <div className="en-stat-value">{stats.total}</div>
            </div>
          </div>
        </Col>
        <Col xs={6} md={4} lg={3}>
          <div className="en-stat-card">
            <div className="en-stat-icon-wrapper en-stat-icon-green">
              <i className="bi bi-patch-check-fill"></i>
            </div>
            <div className="en-stat-content">
              <div className="en-stat-label">KYC OK</div>
              <div className="en-stat-value">{stats.actifs}</div>
            </div>
          </div>
        </Col>
        <Col xs={6} md={4} lg={2}>
          <div className="en-stat-card">
            <div className="en-stat-icon-wrapper en-stat-icon-amber">
              <i className="bi bi-hourglass-split"></i>
            </div>
            <div className="en-stat-content">
              <div className="en-stat-label">KYC KO</div>
              <div className="en-stat-value">{stats.inactifs}</div>
            </div>
          </div>
        </Col>
        <Col xs={6} md={6} lg={2}>
          <div className="en-stat-card">
            <div className="en-stat-icon-wrapper en-stat-icon-cyan">
              <i className="bi bi-shield-fill-check"></i>
            </div>
            <div className="en-stat-content">
              <div className="en-stat-label">Admins</div>
              <div className="en-stat-value">{stats.admins}</div>
            </div>
          </div>
        </Col>
        <Col xs={12} md={6} lg={2}>
          <div className="en-stat-card">
            <div className="en-stat-icon-wrapper en-stat-icon-violet">
              <i className="bi bi-briefcase-fill"></i>
            </div>
            <div className="en-stat-content">
              <div className="en-stat-label">Entreprises</div>
              <div className="en-stat-value">{stats.businesses}</div>
            </div>
          </div>
        </Col>
      </Row>

      {/* ========== FILTRES ========== */}
      <div className="en-filters mb-4">
        <Row className="g-3 align-items-center">
          <Col lg={7} md={12}>
            <div className="en-search-wrapper">
              <i className="bi bi-search en-search-icon"></i>
              <input
                type="text"
                className="en-search-input"
                placeholder="Rechercher par nom, email, téléphone, pays..."
                value={filterText}
                onChange={(e) => { setFilterText(e.target.value); setCurrentPage(1); }}
              />
              {filterText && (
                <button className="en-search-clear" onClick={() => setFilterText('')}>
                  <i className="bi bi-x-lg"></i>
                </button>
              )}
            </div>
          </Col>
          <Col lg={3} md={6}>
            <select
              className="en-select"
              value={itemsPerPage}
              onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
            >
              <option value={10}>10 par page</option>
              <option value={25}>25 par page</option>
              <option value={50}>50 par page</option>
              <option value={100}>100 par page</option>
            </select>
          </Col>
          <Col lg={2} md={6}>
            <button
              className="en-btn-secondary w-100"
              onClick={() => loadEntites(true)}
              disabled={refreshing}
            >
              <i className={`bi ${refreshing ? 'bi-arrow-repeat en-spin' : 'bi-arrow-clockwise'}`}></i>
              <span>Actualiser</span>
            </button>
          </Col>
        </Row>
      </div>

      {/* ========== TABLE ========== */}
      <div className="en-table-card">
        {loadingEntites ? (
          <div className="en-loading">
            <div className="en-loading-spinner"></div>
            <div className="en-loading-text">Chargement des entités...</div>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <Table className="en-table mb-0">
                <thead>
                  <tr>
                    <th style={{ width: '50px' }}>
                      <div className="en-checkbox-wrapper">
                        <input
                          type="checkbox"
                          className="en-checkbox"
                          checked={isAllSelected}
                          ref={input => { if (input) input.indeterminate = isSomeSelected; }}
                          onChange={handleSelectAll}
                        />
                      </div>
                    </th>
                    <SortableHeader field="business_name">
                      <i className="bi bi-building me-1"></i>Entreprise
                    </SortableHeader>
                    <SortableHeader field="business_email">
                      <i className="bi bi-envelope me-1"></i>Email
                    </SortableHeader>
                    <SortableHeader field="phone">
                      <i className="bi bi-telephone me-1"></i>Téléphone
                    </SortableHeader>
                    <SortableHeader field="business_country">
                      <i className="bi bi-globe me-1"></i>Pays
                    </SortableHeader>
                    <SortableHeader field="acc_type">
                      <i className="bi bi-shield-check me-1"></i>Type
                    </SortableHeader>
                    <SortableHeader field="reg_date">
                      <i className="bi bi-calendar me-1"></i>Inscription
                    </SortableHeader>
                    <th><i className="bi bi-patch-check me-1"></i>KYC</th>
                    <th className="text-end" style={{ width: '140px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.length > 0 ? (
                    currentItems.map((entite) => {
                      const isKycOk = entite.kyc === "OK";
                      return (
                        <tr key={entite.business_id} className="en-row">
                          <td>
                            <div className="en-checkbox-wrapper">
                              <input
                                type="checkbox"
                                className="en-checkbox"
                                checked={selectedEntites.includes(entite.business_id)}
                                onChange={() => handleSelectEntite(entite.business_id)}
                              />
                            </div>
                          </td>
                          <td>
                            <div className="en-company-cell">
                              <div className="en-company-avatar">
                                {entite.business_name?.charAt(0).toUpperCase() || '?'}
                              </div>
                              <div className="en-company-content">
                                <span className="en-company-name">{entite.business_name}</span>
                                {entite.firstname && entite.lastname && (
                                  <small className="en-company-owner">
                                    <i className="bi bi-person"></i>
                                    {entite.firstname} {entite.lastname}
                                  </small>
                                )}
                              </div>
                            </div>
                          </td>
                          <td>
                            <span className="en-email-cell">
                              <i className="bi bi-envelope"></i>
                              {entite.business_email}
                            </span>
                          </td>
                          <td>
                            <code className="en-code">{formatPhoneNumber(entite.phone) || 'N/A'}</code>
                          </td>
                          <td>
                            {entite.business_country ? (
                              <span className="en-country-badge">
                                <span className="en-country-flag">{getCountryFlag(entite.business_country)}</span>
                                {getCountryName(entite.business_country)}
                              </span>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td>
                            <span className={`en-type-badge ${getAccountTypeClass(entite.acc_type)}`}>
                              <i className={`bi ${getAccountTypeIcon(entite.acc_type)}`}></i>
                              {getAccountTypeLabel(entite.acc_type)}
                            </span>
                          </td>
                          <td>
                            <span className="en-date-cell">
                              <i className="bi bi-calendar"></i>
                              {formatDate(entite.reg_date)}
                            </span>
                          </td>
                          <td>
                            <span className={`en-kyc-badge ${isKycOk ? 'en-kyc-ok' : 'en-kyc-pending'}`}>
                              <i className={`bi ${isKycOk ? 'bi-patch-check-fill' : 'bi-hourglass-split'}`}></i>
                              {isKycOk ? "Validé" : "En attente"}
                            </span>
                          </td>
                          <td className="text-end">
                            <div className="en-actions">
                              <OverlayTrigger placement="top" overlay={<Tooltip>Modifier</Tooltip>}>
                                <button
                                  className="en-action-btn en-action-edit"
                                  onClick={() => handleEditEntite(entite)}
                                >
                                  <i className="bi bi-pencil"></i>
                                </button>
                              </OverlayTrigger>
                             <OverlayTrigger placement="top" overlay={<Tooltip>Voir détails</Tooltip>}>
  <span className="d-inline-block">
    <button
      type="button"
      className="en-action-btn en-action-view"
      onClick={() => navigate(`/apps/entite/details/${entite.business_id}`)}
      style={{ pointerEvents: 'auto' }}
    >
      <i className="bi bi-eye"></i>
    </button>
  </span>
</OverlayTrigger>
                              <OverlayTrigger placement="top" overlay={<Tooltip>Supprimer</Tooltip>}>
                                <button
                                  className="en-action-btn en-action-delete"
                                  onClick={() => handleDeleteEntite(entite)}
                                >
                                  <i className="bi bi-trash"></i>
                                </button>
                              </OverlayTrigger>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="9">
                        <div className="en-empty-state">
                          <div className="en-empty-icon">
                            <i className="bi bi-buildings"></i>
                          </div>
                          <h5 className="en-empty-title">Aucune entité trouvée</h5>
                          <p className="en-empty-text">
                            {filterText
                              ? "Aucune entité ne correspond à votre recherche"
                              : "Commencez par ajouter votre première entité"}
                          </p>
                          {filterText && (
                            <button className="en-btn-empty-reset" onClick={() => setFilterText('')}>
                              <i className="bi bi-arrow-clockwise me-2"></i>Réinitialiser
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            {/* Pagination */}
            {filteredAndSortedItems.length > 0 && totalPages > 1 && (
              <div className="en-pagination">
                <div className="en-pagination-info">
                  <i className="bi bi-info-circle"></i>
                  Affichage <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, filteredAndSortedItems.length)}</strong> sur <strong>{filteredAndSortedItems.length}</strong>
                  {selectedEntites.length > 0 && (
                    <span className="en-pagination-selected">
                      • <strong>{selectedEntites.length}</strong> sélectionnée{selectedEntites.length > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <div className="en-pagination-controls">
                  <button className="en-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>
                    <i className="bi bi-chevron-double-left"></i>
                  </button>
                  <button className="en-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>
                    <i className="bi bi-chevron-left"></i>
                  </button>
                  {[...Array(Math.min(5, totalPages))].map((_, index) => {
                    const pageNum = currentPage <= 3
                      ? index + 1
                      : currentPage >= totalPages - 2
                        ? totalPages - 4 + index
                        : currentPage - 2 + index;
                    if (pageNum > 0 && pageNum <= totalPages) {
                      return (
                        <button
                          key={pageNum}
                          className={`en-page-btn ${currentPage === pageNum ? 'active' : ''}`}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                    return null;
                  })}
                  <button className="en-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>
                    <i className="bi bi-chevron-right"></i>
                  </button>
                  <button className="en-page-btn" disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>
                    <i className="bi bi-chevron-double-right"></i>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ========== MODAL AJOUT ========== */}
      <Modal show={showAddEntiteModal} onHide={() => setShowAddEntiteModal(false)} size="lg" centered className="en-modal">
        <div className="en-modal-header en-modal-header-success">
          <div className="d-flex align-items-center gap-3">
            <div className="en-modal-icon">
              <i className="bi bi-building-add"></i>
            </div>
            <div>
              <h5 className="en-modal-title mb-0">Nouvelle entité</h5>
              <small className="en-modal-subtitle">Ajouter une entreprise</small>
            </div>
          </div>
          <button className="en-modal-close" onClick={() => setShowAddEntiteModal(false)}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <Modal.Body className="en-modal-body">
          <Row className="g-3">
            <Col md={12}>
              <div className="en-form-group">
                <label className="en-form-label">
                  Nom de l'entreprise <span className="en-required">*</span>
                </label>
                <input
                  type="text"
                  className="en-input"
                  value={newEntite.business_name}
                  onChange={(e) => setNewEntite({ ...newEntite, business_name: e.target.value })}
                  placeholder="Entrez le nom de l'entreprise"
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="en-form-group">
                <label className="en-form-label">
                  Email professionnel <span className="en-required">*</span>
                </label>
                <input
                  type="email"
                  className="en-input"
                  value={newEntite.business_email}
                  onChange={(e) => setNewEntite({ ...newEntite, business_email: e.target.value })}
                  placeholder="contact@entreprise.com"
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="en-form-group">
                <label className="en-form-label">Téléphone</label>
                <input
                  type="tel"
                  className="en-input"
                  value={newEntite.phone}
                  onChange={(e) => setNewEntite({ ...newEntite, phone: e.target.value })}
                  placeholder="+241 XX XX XX XX"
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="en-form-group">
                <label className="en-form-label">Pays</label>
                <input
                  type="text"
                  className="en-input"
                  value={newEntite.business_country}
                  onChange={(e) => setNewEntite({ ...newEntite, business_country: e.target.value })}
                  placeholder="Gabon"
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="en-form-group">
                <label className="en-form-label">Ville</label>
                <input
                  type="text"
                  className="en-input"
                  value={newEntite.business_city}
                  onChange={(e) => setNewEntite({ ...newEntite, business_city: e.target.value })}
                  placeholder="Libreville"
                />
              </div>
            </Col>
            <Col md={12}>
              <div className="en-form-group">
                <label className="en-form-label">Type de compte</label>
                <select
                  className="en-select w-100"
                  value={newEntite.acc_type}
                  onChange={(e) => setNewEntite({ ...newEntite, acc_type: e.target.value })}
                >
                  <option value="business">Entreprise</option>
                  <option value="admin">Administrateur</option>
                  <option value="system">Système</option>
                </select>
                <div className="en-form-hint">
                  <i className="bi bi-info-circle"></i>
                  Sélectionnez le type de compte pour cette entité
                </div>
              </div>
            </Col>
          </Row>
        </Modal.Body>

        <div className="en-modal-footer">
          <button className="en-btn-cancel" onClick={() => setShowAddEntiteModal(false)}>Annuler</button>
          <button
            className="en-btn-success"
            onClick={handleSaveEntite}
            disabled={createLoading || !newEntite.business_name.trim() || !newEntite.business_email.trim()}
          >
            {createLoading ? (
              <><Spinner animation="border" size="sm" /> Création...</>
            ) : (
              <><i className="bi bi-check-lg"></i> Enregistrer</>
            )}
          </button>
        </div>
      </Modal>

      {/* ========== MODAL ÉDITION ========== */}
      <Modal show={showEditEntiteModal} onHide={() => setShowEditEntiteModal(false)} size="lg" centered className="en-modal">
        <div className="en-modal-header en-modal-header-blue">
          <div className="d-flex align-items-center gap-3">
            <div className="en-modal-icon">
              <i className="bi bi-pencil-square"></i>
            </div>
            <div>
              <h5 className="en-modal-title mb-0">Modifier l'entité</h5>
              <small className="en-modal-subtitle">{selectedEntite?.business_name}</small>
            </div>
          </div>
          <button className="en-modal-close" onClick={() => setShowEditEntiteModal(false)}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <Modal.Body className="en-modal-body">
          {selectedEntite && (
            <Row className="g-3">
              <Col md={12}>
                <div className="en-form-group">
                  <label className="en-form-label">
                    Nom de l'entreprise <span className="en-required">*</span>
                  </label>
                  <input
                    type="text"
                    className="en-input"
                    value={editEntite.business_name}
                    onChange={(e) => setEditEntite({ ...editEntite, business_name: e.target.value })}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="en-form-group">
                  <label className="en-form-label">
                    Email professionnel <span className="en-required">*</span>
                  </label>
                  <input
                    type="email"
                    className="en-input"
                    value={editEntite.business_email}
                    onChange={(e) => setEditEntite({ ...editEntite, business_email: e.target.value })}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="en-form-group">
                  <label className="en-form-label">Téléphone</label>
                  <input
                    type="tel"
                    className="en-input"
                    value={editEntite.phone}
                    onChange={(e) => setEditEntite({ ...editEntite, phone: e.target.value })}
                  />
                </div>
              </Col>
              <Col md={6}>
                <div className="en-form-group">
                  <label className="en-form-label">Pays</label>
                  <select
                    className="en-select w-100"
                    value={editEntite.business_country || "GA"}
                    onChange={(e) => setEditEntite({ ...editEntite, business_country: e.target.value })}
                  >
                    <option value="GA">Gabon</option>
                    <option value="SN">Sénégal</option>
                    <option value="CG">Congo</option>
                    <option value="CI">Côte d'Ivoire</option>
                  </select>
                </div>
              </Col>
              <Col md={6}>
                <div className="en-form-group">
                  <label className="en-form-label">Ville</label>
                  <input
                    type="text"
                    className="en-input"
                    value={editEntite.business_city}
                    onChange={(e) => setEditEntite({ ...editEntite, business_city: e.target.value })}
                  />
                </div>
              </Col>
              <Col md={12}>
                <div className="en-form-group">
                  <label className="en-form-label">Type de compte</label>
                  <select
                    className="en-select w-100"
                    value={editEntite.acc_type}
                    onChange={(e) => setEditEntite({ ...editEntite, acc_type: e.target.value })}
                  >
                    <option value="business">Entreprise</option>
                    <option value="admin">Administrateur</option>
                    <option value="system">Système</option>
                  </select>
                </div>
              </Col>
            </Row>
          )}
        </Modal.Body>

        <div className="en-modal-footer">
          <button className="en-btn-cancel" onClick={() => setShowEditEntiteModal(false)}>Annuler</button>
          <button
            className="en-btn-primary"
            onClick={handleUpdateEntite}
            disabled={updateLoading || !editEntite.business_name.trim() || !editEntite.business_email.trim()}
          >
            {updateLoading ? (
              <><Spinner animation="border" size="sm" /> Mise à jour...</>
            ) : (
              <><i className="bi bi-check-lg"></i> Mettre à jour</>
            )}
          </button>
        </div>
      </Modal>

      {/* ========== MODAL DÉTAILS ========== */}
      <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg" centered className="en-modal">
        <div className="en-modal-header en-modal-header-info">
          <div className="d-flex align-items-center gap-3">
            <div className="en-modal-icon">
              <i className="bi bi-building-fill"></i>
            </div>
            <div>
              <h5 className="en-modal-title mb-0">Détails de l'entité</h5>
              <small className="en-modal-subtitle">Informations complètes</small>
            </div>
          </div>
          <button className="en-modal-close" onClick={() => setShowDetailsModal(false)}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <Modal.Body className="en-modal-body">
          {selectedEntite && (
            <>
              {/* HERO */}
              <div className="en-detail-hero">
                <div className="en-detail-avatar">
                  {selectedEntite.business_name?.charAt(0).toUpperCase() || '?'}
                </div>
                <div className="en-detail-hero-content">
                  <div className="en-detail-hero-name">{selectedEntite.business_name}</div>
                  <div className="en-detail-hero-meta">
                    <span><i className="bi bi-envelope"></i>{selectedEntite.business_email}</span>
                    <span><i className="bi bi-telephone"></i>{formatPhoneNumber(selectedEntite.phone) || 'Non renseigné'}</span>
                  </div>
                </div>
                <span className={`en-kyc-badge ${selectedEntite.kyc === 'OK' ? 'en-kyc-ok' : 'en-kyc-pending'}`}>
                  <i className={`bi ${selectedEntite.kyc === 'OK' ? 'bi-patch-check-fill' : 'bi-hourglass-split'}`}></i>
                  {selectedEntite.kyc === 'OK' ? "Validé" : "En attente"}
                </span>
              </div>

              {/* GRID */}
              <div className="en-detail-grid">
                <div className="en-detail-card">
                  <div className="en-detail-card-icon en-detail-card-icon-blue">
                    <i className="bi bi-geo-alt"></i>
                  </div>
                  <div className="en-detail-card-content">
                    <div className="en-detail-card-label">Pays</div>
                    <div className="en-detail-card-value">
                      {getCountryFlag(selectedEntite.business_country)} {getCountryName(selectedEntite.business_country) || 'Non renseigné'}
                    </div>
                  </div>
                </div>
                <div className="en-detail-card">
                  <div className="en-detail-card-icon en-detail-card-icon-violet">
                    <i className="bi bi-building"></i>
                  </div>
                  <div className="en-detail-card-content">
                    <div className="en-detail-card-label">Ville</div>
                    <div className="en-detail-card-value">{selectedEntite.business_city || 'Non renseignée'}</div>
                  </div>
                </div>
                <div className="en-detail-card">
                  <div className="en-detail-card-icon en-detail-card-icon-cyan">
                    <i className="bi bi-shield-check"></i>
                  </div>
                  <div className="en-detail-card-content">
                    <div className="en-detail-card-label">Type de compte</div>
                    <div className="en-detail-card-value">
                      <span className={`en-type-badge ${getAccountTypeClass(selectedEntite.acc_type)}`}>
                        <i className={`bi ${getAccountTypeIcon(selectedEntite.acc_type)}`}></i>
                        {getAccountTypeLabel(selectedEntite.acc_type)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="en-detail-card">
                  <div className="en-detail-card-icon en-detail-card-icon-amber">
                    <i className="bi bi-calendar"></i>
                  </div>
                  <div className="en-detail-card-content">
                    <div className="en-detail-card-label">Date d'inscription</div>
                    <div className="en-detail-card-value">{formatDate(selectedEntite.reg_date)}</div>
                  </div>
                </div>
                <div className="en-detail-card">
                  <div className="en-detail-card-icon en-detail-card-icon-green">
                    <i className="bi bi-key"></i>
                  </div>
                  <div className="en-detail-card-content">
                    <div className="en-detail-card-label">MFA activé</div>
                    <div className="en-detail-card-value">
                      <span className={`en-mfa-badge ${selectedEntite.has_mfa === 'YES' ? 'en-mfa-yes' : 'en-mfa-no'}`}>
                        {selectedEntite.has_mfa === 'YES' ? 'Oui' : 'Non'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="en-detail-card">
                  <div className="en-detail-card-icon en-detail-card-icon-gray">
                    <i className="bi bi-hash"></i>
                  </div>
                  <div className="en-detail-card-content">
                    <div className="en-detail-card-label">Business ID</div>
                    <div className="en-detail-card-value">{selectedEntite.business_id || 'Non attribué'}</div>
                  </div>
                </div>
              </div>

              {/* INFO TECHNIQUE */}
              <div className="en-detail-section">
                <div className="en-detail-section-header">
                  <i className="bi bi-info-circle"></i>
                  <span>Informations complémentaires</span>
                </div>
                <div className="en-detail-section-body">
                  {selectedEntite.firstname && selectedEntite.lastname && (
                    <div className="en-tech-row">
                      <span className="en-tech-label">Responsable</span>
                      <span className="en-tech-value">{selectedEntite.firstname} {selectedEntite.lastname}</span>
                    </div>
                  )}
                  <div className="en-tech-row">
                    <span className="en-tech-label">Code pays</span>
                    <span className="en-tech-value">{selectedEntite.country_code || 'Non renseigné'}</span>
                  </div>
                  <div className="en-tech-row">
                    <span className="en-tech-label">Statut KYC</span>
                    <span className="en-tech-value">
                      <span className={`en-kyc-badge ${selectedEntite.kyc === 'OK' ? 'en-kyc-ok' : 'en-kyc-pending'}`}>
                        {selectedEntite.kyc === 'OK' ? "Validé" : "En attente"}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </Modal.Body>

        <div className="en-modal-footer">
          <button className="en-btn-cancel" onClick={() => setShowDetailsModal(false)}>Fermer</button>
          {selectedEntite && (
            <>
              <button
                className="en-btn-warning"
                onClick={() => {
                  setShowDetailsModal(false);
                  handleGoToPricing(selectedEntite);
                }}
              >
                <i className="bi bi-currency-euro"></i> Tarification
              </button>
              <button
                className="en-btn-primary"
                onClick={() => {
                  setShowDetailsModal(false);
                  handleEditEntite(selectedEntite);
                }}
              >
                <i className="bi bi-pencil"></i> Modifier
              </button>
            </>
          )}
        </div>
      </Modal>

      {/* ========== MODAL SUPPRESSION ========== */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered className="en-modal">
        <Modal.Body className="en-delete-body">
          <div className="en-delete-icon-wrapper">
            <div className="en-delete-icon">
              <i className="bi bi-trash"></i>
            </div>
          </div>
          <h4 className="en-delete-title">Confirmer la suppression</h4>
          <p className="en-delete-text">Êtes-vous sûr de vouloir supprimer cette entité ?</p>

          {entiteToDelete && (
            <div className="en-delete-entity">
              <div className="en-delete-avatar">
                {entiteToDelete.business_name?.charAt(0).toUpperCase() || '?'}
              </div>
              <div className="en-delete-info">
                <div className="en-delete-name">{entiteToDelete.business_name}</div>
                <div className="en-delete-meta">
                  <span><i className="bi bi-envelope"></i>{entiteToDelete.business_email}</span>
                  {entiteToDelete.phone && (
                    <span><i className="bi bi-telephone"></i>{formatPhoneNumber(entiteToDelete.phone)}</span>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="en-delete-warning">
            <i className="bi bi-exclamation-triangle"></i>
            Cette action est irréversible et supprimera définitivement l'entité et toutes ses données.
          </div>

          <div className="en-delete-actions">
            <button className="en-btn-cancel en-btn-flex" onClick={() => setShowDeleteModal(false)} disabled={deleteLoading}>
              <i className="bi bi-x-lg"></i> Annuler
            </button>
            <button className="en-btn-danger en-btn-flex" onClick={handleConfirmDelete} disabled={deleteLoading}>
              {deleteLoading ? (
                <><Spinner animation="border" size="sm" /> Suppression...</>
              ) : (
                <><i className="bi bi-trash"></i> Supprimer</>
              )}
            </button>
          </div>
        </Modal.Body>
      </Modal>

      {/* ========== MODAL SUPPRESSION MULTIPLE ========== */}
      <Modal show={showBulkDeleteModal} onHide={() => setShowBulkDeleteModal(false)} centered className="en-modal">
        <Modal.Body className="en-delete-body">
          <div className="en-delete-icon-wrapper">
            <div className="en-delete-icon">
              <i className="bi bi-trash"></i>
            </div>
          </div>
          <h4 className="en-delete-title">Suppression multiple</h4>
          <p className="en-delete-text">
            Vous êtes sur le point de supprimer <strong>{selectedEntites.length} entité(s)</strong>.
          </p>
          <div className="en-delete-warning">
            <i className="bi bi-exclamation-triangle"></i>
            Cette action est irréversible
          </div>
          <div className="en-delete-actions">
            <button className="en-btn-cancel en-btn-flex" onClick={() => setShowBulkDeleteModal(false)} disabled={bulkDeleteLoading}>
              <i className="bi bi-x-lg"></i> Annuler
            </button>
            <button className="en-btn-danger en-btn-flex" onClick={handleConfirmBulkDelete} disabled={bulkDeleteLoading}>
              {bulkDeleteLoading ? (
                <><Spinner animation="border" size="sm" /> Suppression...</>
              ) : (
                <><i className="bi bi-trash"></i> Supprimer ({selectedEntites.length})</>
              )}
            </button>
          </div>
        </Modal.Body>
      </Modal>

      <Footer />

      {/* ========== STYLES PREMIUM ========== */}
      <style>{`
        /* ========== GLOBAL ========== */
        .en-page { background: #f8fafc; min-height: 100vh; }
        .en-page * { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }

        /* ========== HEADER ========== */
        .en-header {
          background: linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%);
          border-radius: 16px; padding: 24px 28px;
          border: 1px solid #e2e8f0; box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .en-header-icon {
          width: 56px; height: 56px; border-radius: 14px;
          background: linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%);
          color: white; display: flex; align-items: center; justify-content: center;
          font-size: 26px; box-shadow: 0 8px 20px -6px rgba(14, 165, 233, 0.5);
        }
        .en-header-title {
          font-weight: 700; color: #0f172a; font-size: 1.5rem; letter-spacing: -0.02em;
        }
        .en-header-subtitle { color: #64748b; font-size: 0.875rem; }

        /* ========== BUTTONS ========== */
        .en-btn-primary, .en-btn-secondary, .en-btn-cancel, .en-btn-danger, .en-btn-success, .en-btn-warning {
          display: inline-flex; align-items: center; justify-content: center; gap: 8px;
          padding: 11px 20px; border-radius: 10px; font-weight: 600; font-size: 0.875rem;
          border: none; transition: all 0.2s; cursor: pointer; text-decoration: none;
        }
        .en-btn-primary {
          background: linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%); color: white;
        }
        .en-btn-primary:hover:not(:disabled) {
          background: linear-gradient(135deg, #0284c7 0%, #2563eb 100%);
          transform: translateY(-1px); box-shadow: 0 8px 16px -4px rgba(14, 165, 233, 0.4);
        }
        .en-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
        .en-btn-secondary {
          background: white; border: 1px solid #e2e8f0; color: #334155;
        }
        .en-btn-secondary:hover:not(:disabled) {
          background: #f8fafc; border-color: #cbd5e1; color: #0f172a;
        }
        .en-btn-secondary:disabled { opacity: 0.6; }
        .en-btn-danger {
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); color: white;
        }
        .en-btn-danger:hover:not(:disabled) {
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
          transform: translateY(-1px); box-shadow: 0 8px 16px -4px rgba(239, 68, 68, 0.4);
        }
        .en-btn-success {
          background: linear-gradient(135deg, #10b981 0%, #059669 100%); color: white;
        }
        .en-btn-success:hover:not(:disabled) {
          background: linear-gradient(135deg, #059669 0%, #047857 100%);
          transform: translateY(-1px); box-shadow: 0 8px 16px -4px rgba(16, 185, 129, 0.4);
        }
        .en-btn-warning {
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); color: white;
        }
        .en-btn-warning:hover:not(:disabled) {
          background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
          transform: translateY(-1px);
        }
        .en-btn-cancel {
          background: white; border: 1px solid #e2e8f0; color: #334155;
        }
        .en-btn-cancel:hover { background: #f1f5f9; }
        .en-btn-flex { flex: 1; }
        .en-dropdown-menu {
          border: 1px solid #e2e8f0; border-radius: 10px;
          box-shadow: 0 8px 24px -8px rgba(0,0,0,0.1); padding: 6px;
        }
        .en-dropdown-menu .dropdown-item {
          border-radius: 6px; padding: 8px 12px; font-size: 0.85rem; display: flex; align-items: center;
        }
        .en-dropdown-menu .dropdown-item:hover { background: #f1f5f9; }

        /* ========== ALERTS ========== */
        .en-alert {
          display: flex; align-items: center; gap: 12px; padding: 14px 18px;
          border-radius: 12px; font-size: 0.85rem; font-weight: 500;
        }
        .en-alert > i { font-size: 1.3rem; flex-shrink: 0; }
        .en-alert-success { background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%); color: #065f46; border-left: 4px solid #10b981; }
        .en-alert-danger { background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%); color: #991b1b; border-left: 4px solid #ef4444; }
        .en-alert-warning { background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%); color: #92400e; border-left: 4px solid #f59e0b; }
        .en-alert-info { background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%); color: #1e40af; border-left: 4px solid #3b82f6; }
        .en-alert-close {
          background: none; border: none; color: inherit; opacity: 0.6; cursor: pointer;
          padding: 4px 8px; border-radius: 6px; font-size: 1rem;
        }
        .en-alert-close:hover { opacity: 1; background: rgba(0,0,0,0.05); }

        /* ========== STATS ========== */
        .en-stat-card {
          display: flex; align-items: center; gap: 12px; background: white;
          border-radius: 14px; padding: 16px; border: 1px solid #e2e8f0;
          transition: all 0.25s; height: 100%;
        }
        .en-stat-card:hover { transform: translateY(-2px); box-shadow: 0 8px 20px -6px rgba(0,0,0,0.08); }
        .en-stat-icon-wrapper {
          width: 42px; height: 42px; border-radius: 11px;
          display: flex; align-items: center; justify-content: center;
          font-size: 18px; flex-shrink: 0;
        }
        .en-stat-icon-blue   { background: #dbeafe; color: #3b82f6; }
        .en-stat-icon-green  { background: #d1fae5; color: #10b981; }
        .en-stat-icon-amber  { background: #fef3c7; color: #d97706; }
        .en-stat-icon-cyan   { background: #cffafe; color: #0891b2; }
        .en-stat-icon-violet { background: #ede9fe; color: #8b5cf6; }
        .en-stat-content { min-width: 0; flex-grow: 1; }
        .en-stat-label {
          font-size: 0.65rem; color: #64748b; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 3px;
        }
        .en-stat-value {
          font-size: 1.35rem; font-weight: 700; color: #0f172a;
          letter-spacing: -0.02em; line-height: 1;
        }

        /* ========== FILTRES ========== */
        .en-filters {
          background: white; border-radius: 14px; padding: 16px 20px; border: 1px solid #e2e8f0;
        }
        .en-search-wrapper { position: relative; display: flex; align-items: center; }
        .en-search-icon {
          position: absolute; left: 14px; color: #94a3b8; font-size: 1.05rem; pointer-events: none;
        }
        .en-search-input {
          width: 100%; padding: 11px 40px 11px 42px; border: 1px solid #e2e8f0;
          border-radius: 10px; font-size: 0.875rem; background: #f8fafc;
          transition: all 0.2s; outline: none; font-family: inherit;
        }
        .en-search-input:focus {
          background: white; border-color: #0ea5e9; box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
        }
        .en-search-clear {
          position: absolute; right: 8px; background: none; border: none; color: #94a3b8;
          font-size: 1rem; padding: 6px 10px; cursor: pointer; border-radius: 6px; transition: all 0.15s;
        }
        .en-search-clear:hover { background: #fee2e2; color: #ef4444; }
        .en-select {
          width: 100%; padding: 11px 14px; border: 1px solid #e2e8f0;
          border-radius: 10px; font-size: 0.875rem; font-weight: 500;
          background: #f8fafc; color: #334155; outline: none; cursor: pointer; font-family: inherit;
        }
        .en-select:focus {
          background: white; border-color: #0ea5e9; box-shadow: 0 0 0 3px rgba(14, 165, 233, 0.1);
        }

        /* ========== TABLE ========== */
        .en-table-card {
          background: white; border-radius: 16px; border: 1px solid #e2e8f0;
          overflow: hidden; box-shadow: 0 1px 3px rgba(0,0,0,0.03);
        }
        .en-table { font-size: 0.875rem; }
        .en-table thead th {
          background: #f8fafc; color: #475569; font-weight: 700; font-size: 0.7rem;
          text-transform: uppercase; letter-spacing: 0.05em; padding: 16px;
          border-bottom: 1px solid #e2e8f0; border-top: none; white-space: nowrap;
        }
        .en-table tbody td {
          padding: 16px; vertical-align: middle; border-bottom: 1px solid #f1f5f9;
        }
        .en-row { transition: background-color 0.15s; }
        .en-row:hover { background-color: #f8fafc; }
        .en-sortable-header { cursor: pointer; user-select: none; }
        .en-sortable-header:hover { color: #0ea5e9; }
        .en-sortable-content { display: inline-flex; align-items: center; gap: 6px; }
        .en-sort-icon { color: #0ea5e9; font-size: 0.75rem; }

        .en-checkbox-wrapper { display: flex; align-items: center; justify-content: center; }
        .en-checkbox {
          width: 18px; height: 18px; border-radius: 5px; border: 1.5px solid #cbd5e1;
          cursor: pointer; accent-color: #0ea5e9;
        }

        /* Company cell */
        .en-company-cell { display: flex; align-items: center; gap: 12px; }
        .en-company-avatar {
          width: 38px; height: 38px; border-radius: 10px;
          background: linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%);
          color: white; display: flex; align-items: center; justify-content: center;
          font-weight: 700; font-size: 15px; flex-shrink: 0;
        }
        .en-company-content { min-width: 0; }
        .en-company-name { font-weight: 700; color: #0f172a; display: block; }
        .en-company-owner {
          color: #94a3b8; font-size: 0.72rem;
          display: flex; align-items: center; gap: 4px;
        }

        /* Email cell */
        .en-email-cell {
          display: inline-flex; align-items: center; gap: 6px;
          color: #334155; font-size: 0.82rem;
        }
        .en-email-cell i { color: #94a3b8; }

        /* Code */
        .en-code {
          font-size: 0.78rem; color: #0e7490; background: #cffafe;
          padding: 4px 8px; border-radius: 5px; font-weight: 600;
          font-family: 'SF Mono', Menlo, Monaco, monospace;
        }

        /* Country badge */
        .en-country-badge {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 5px 10px; background: #f1f5f9;
          border-radius: 6px; color: #475569;
          font-size: 0.75rem; font-weight: 600;
        }
        .en-country-flag { font-size: 0.95rem; }

        /* Account type badge */
        .en-type-badge {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 5px 11px; border-radius: 6px;
          font-size: 0.7rem; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.03em;
        }
        .en-type-admin    { background: #ede9fe; color: #5b21b6; }
        .en-type-business { background: #dbeafe; color: #1e40af; }
        .en-type-system   { background: #f1f5f9; color: #475569; }
        .en-type-default  { background: #fef3c7; color: #92400e; }

        /* Date cell */
        .en-date-cell {
          display: inline-flex; align-items: center; gap: 6px;
          color: #64748b; font-size: 0.8rem; font-variant-numeric: tabular-nums;
        }
        .en-date-cell i { color: #94a3b8; }

        /* KYC badge */
        .en-kyc-badge {
          display: inline-flex; align-items: center; gap: 5px;
          padding: 5px 11px; border-radius: 6px;
          font-size: 0.72rem; font-weight: 700;
        }
        .en-kyc-ok { background: #d1fae5; color: #059669; }
        .en-kyc-pending { background: #fef3c7; color: #92400e; }

        /* MFA badge */
        .en-mfa-badge {
          display: inline-flex; align-items: center;
          padding: 4px 10px; border-radius: 6px;
          font-size: 0.72rem; font-weight: 700;
        }
        .en-mfa-yes { background: #d1fae5; color: #059669; }
        .en-mfa-no { background: #f1f5f9; color: #64748b; }

        /* Actions */
        .en-actions { display: inline-flex; gap: 6px; justify-content: flex-end; }
        .en-action-btn {
          width: 34px; height: 34px; border-radius: 8px; border: 1px solid #e2e8f0;
          background: white; color: #64748b; display: inline-flex;
          align-items: center; justify-content: center; cursor: pointer;
          transition: all 0.15s; font-size: 0.9rem;
        }
        .en-action-edit:hover {
          background: #0ea5e9; border-color: #0ea5e9; color: white;
          transform: translateY(-1px); box-shadow: 0 4px 12px -2px rgba(14, 165, 233, 0.4);
        }
        .en-action-view:hover {
          background: #06b6d4; border-color: #06b6d4; color: white;
          transform: translateY(-1px); box-shadow: 0 4px 12px -2px rgba(6, 182, 212, 0.4);
        }
        .en-action-delete:hover {
          background: #ef4444; border-color: #ef4444; color: white;
          transform: translateY(-1px); box-shadow: 0 4px 12px -2px rgba(239, 68, 68, 0.4);
        }

        /* ========== LOADING / EMPTY ========== */
        .en-loading { padding: 80px 20px; text-align: center; }
        .en-loading-spinner {
          width: 48px; height: 48px; border: 4px solid #e2e8f0;
          border-top-color: #0ea5e9; border-radius: 50%;
          animation: en-spin 0.8s linear infinite; margin: 0 auto 16px;
        }
        .en-loading-text { color: #64748b; font-size: 0.875rem; font-weight: 500; }
        .en-empty-state { padding: 60px 20px; text-align: center; }
        .en-empty-icon {
          width: 80px; height: 80px; border-radius: 50%; background: #f1f5f9; color: #94a3b8;
          display: inline-flex; align-items: center; justify-content: center;
          font-size: 36px; margin-bottom: 20px;
        }
        .en-empty-title { color: #334155; font-weight: 700; margin-bottom: 8px; }
        .en-empty-text { color: #94a3b8; margin-bottom: 20px; }
        .en-btn-empty-reset {
          display: inline-flex; align-items: center; background: white; color: #0ea5e9;
          border: 1px solid #bae6fd; border-radius: 8px; font-weight: 600;
          padding: 8px 16px; cursor: pointer; transition: all 0.2s;
        }
        .en-btn-empty-reset:hover { background: #0ea5e9; color: white; border-color: #0ea5e9; }

        /* ========== PAGINATION ========== */
        .en-pagination {
          display: flex; justify-content: space-between; align-items: center;
          padding: 16px 24px; background: #f8fafc; border-top: 1px solid #e2e8f0;
          flex-wrap: wrap; gap: 12px;
        }
        .en-pagination-info {
          font-size: 0.8rem; color: #64748b; display: flex; align-items: center; gap: 6px;
        }
        .en-pagination-info strong { color: #0f172a; }
        .en-pagination-selected { color: #0ea5e9; margin-left: 8px; }
        .en-pagination-controls { display: flex; align-items: center; gap: 6px; }
        .en-page-btn {
          min-width: 34px; height: 34px; padding: 0 10px; border-radius: 8px;
          border: 1px solid #e2e8f0; background: white; color: #475569;
          display: inline-flex; align-items: center; justify-content: center;
          cursor: pointer; transition: all 0.15s; font-size: 0.8rem; font-weight: 600;
        }
        .en-page-btn:hover:not(:disabled) { background: #0ea5e9; color: white; border-color: #0ea5e9; }
        .en-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
        .en-page-btn.active { background: #0ea5e9; color: white; border-color: #0ea5e9; }

        /* ========== MODAL ========== */
        .en-modal .modal-content { border: none; border-radius: 16px; overflow: hidden; }
        .en-modal-header {
          display: flex; justify-content: space-between; align-items: center;
          padding: 20px 24px; color: white;
        }
        .en-modal-header-success { background: linear-gradient(135deg, #10b981 0%, #059669 100%); }
        .en-modal-header-blue { background: linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%); }
        .en-modal-header-info { background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%); }
        .en-modal-icon {
          width: 44px; height: 44px; border-radius: 10px; background: rgba(255,255,255,0.2);
          display: flex; align-items: center; justify-content: center; font-size: 20px;
        }
        .en-modal-title { font-weight: 700; letter-spacing: -0.01em; margin: 0; }
        .en-modal-subtitle { color: rgba(255,255,255,0.85); font-size: 0.8rem; }
        .en-modal-close {
          background: rgba(255,255,255,0.1); border: none; color: white;
          width: 36px; height: 36px; border-radius: 10px; display: flex;
          align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; font-size: 1rem;
        }
        .en-modal-close:hover { background: rgba(255,255,255,0.2); transform: rotate(90deg); }
        .en-modal-body {
          padding: 24px; background: #f8fafc; max-height: 70vh; overflow-y: auto;
        }
        .en-modal-footer {
          padding: 16px 24px; background: white; border-top: 1px solid #e2e8f0;
          display: flex; justify-content: flex-end; gap: 10px;
        }

        /* Detail Hero */
        .en-detail-hero {
          display: flex; align-items: center; gap: 16px; padding: 20px;
          background: white; border-radius: 14px; border: 1px solid #e2e8f0;
          margin-bottom: 16px; flex-wrap: wrap;
        }
        .en-detail-avatar {
          width: 64px; height: 64px; border-radius: 16px;
          background: linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%);
          color: white; display: flex; align-items: center; justify-content: center;
          font-weight: 700; font-size: 26px; flex-shrink: 0;
          box-shadow: 0 8px 16px -4px rgba(14, 165, 233, 0.3);
        }
        .en-detail-hero-content { flex-grow: 1; min-width: 0; }
        .en-detail-hero-name {
          font-weight: 700; color: #0f172a; font-size: 1.15rem; margin-bottom: 6px;
        }
        .en-detail-hero-meta {
          display: flex; flex-wrap: wrap; gap: 14px; font-size: 0.82rem; color: #64748b;
        }
        .en-detail-hero-meta span { display: inline-flex; align-items: center; gap: 5px; }
        .en-detail-hero-meta i { color: #94a3b8; }

        /* Detail Grid */
        .en-detail-grid {
          display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; margin-bottom: 16px;
        }
        .en-detail-card {
          display: flex; align-items: center; gap: 14px; padding: 16px;
          background: white; border-radius: 12px; border: 1px solid #e2e8f0;
          transition: all 0.2s;
        }
        .en-detail-card:hover { transform: translateY(-2px); box-shadow: 0 8px 16px -6px rgba(0, 0, 0, 0.08); }
        .en-detail-card-icon {
          width: 42px; height: 42px; border-radius: 10px; display: flex;
          align-items: center; justify-content: center; font-size: 18px; flex-shrink: 0;
        }
        .en-detail-card-icon-blue   { background: #dbeafe; color: #3b82f6; }
        .en-detail-card-icon-violet { background: #ede9fe; color: #8b5cf6; }
        .en-detail-card-icon-cyan   { background: #cffafe; color: #0891b2; }
        .en-detail-card-icon-amber  { background: #fef3c7; color: #d97706; }
        .en-detail-card-icon-green  { background: #d1fae5; color: #059669; }
        .en-detail-card-icon-gray   { background: #f1f5f9; color: #64748b; }
        .en-detail-card-content { flex-grow: 1; min-width: 0; }
        .en-detail-card-label {
          font-size: 0.68rem; color: #94a3b8; font-weight: 700;
          text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 4px;
        }
        .en-detail-card-value { font-weight: 700; color: #0f172a; font-size: 0.85rem; }

        /* Detail Section */
        .en-detail-section {
          background: white; border-radius: 12px; border: 1px solid #e2e8f0; overflow: hidden;
        }
        .en-detail-section-header {
          display: flex; align-items: center; gap: 8px; padding: 14px 18px;
          background: #f8fafc; border-bottom: 1px solid #e2e8f0;
          font-weight: 700; color: #0f172a; font-size: 0.85rem;
        }
        .en-detail-section-header i { color: #0ea5e9; font-size: 1rem; }
        .en-detail-section-body { padding: 16px 18px; }
        .en-tech-row {
          display: flex; justify-content: space-between; align-items: center;
          padding: 10px 0; border-bottom: 1px solid #f1f5f9;
        }
        .en-tech-row:last-child { border-bottom: none; }
        .en-tech-label {
          font-size: 0.78rem; color: #94a3b8; font-weight: 600;
          text-transform: uppercase; letter-spacing: 0.03em;
        }
        .en-tech-value { font-size: 0.85rem; color: #0f172a; font-weight: 600; }

        /* Form */
        .en-form-group { margin-bottom: 4px; }
        .en-form-label {
          display: block; font-weight: 700; color: #0f172a; font-size: 0.82rem; margin-bottom: 8px;
        }
        .en-required { color: #ef4444; }
        .en-input {
          width: 100%; padding: 11px 14px; border: 2px solid #e2e8f0;
          border-radius: 10px; font-size: 0.875rem; color: #0f172a; background: white;
          transition: all 0.2s; outline: none; font-family: inherit;
        }
        .en-input:focus { border-color: #0ea5e9; box-shadow: 0 0 0 4px rgba(14, 165, 233, 0.1); }
        .en-form-hint {
          margin-top: 6px; font-size: 0.72rem; color: #94a3b8;
          display: flex; align-items: center; gap: 4px;
        }
        .en-form-hint i { color: #0ea5e9; }

        /* Delete modal */
        .en-delete-body { padding: 32px 24px 24px; text-align: center; }
        .en-delete-icon-wrapper { margin-bottom: 20px; }
        .en-delete-icon {
          width: 80px; height: 80px; border-radius: 50%;
          background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
          color: #dc2626; display: inline-flex; align-items: center; justify-content: center;
          font-size: 36px; box-shadow: 0 12px 24px -8px rgba(220, 38, 38, 0.3);
        }
        .en-delete-title { font-weight: 700; color: #0f172a; margin-bottom: 12px; font-size: 1.25rem; }
        .en-delete-text { color: #64748b; margin-bottom: 20px; font-size: 0.875rem; }
        .en-delete-entity {
          display: flex; align-items: center; gap: 14px;
          background: #f8fafc; border-radius: 12px; padding: 16px 20px;
          margin-bottom: 16px; border: 1px solid #e2e8f0; text-align: left;
        }
        .en-delete-avatar {
          width: 48px; height: 48px; border-radius: 12px;
          background: linear-gradient(135deg, #0ea5e9 0%, #3b82f6 100%);
          color: white; display: flex; align-items: center; justify-content: center;
          font-weight: 700; font-size: 18px; flex-shrink: 0;
        }
        .en-delete-info { min-width: 0; flex-grow: 1; }
        .en-delete-name { font-weight: 700; color: #0f172a; font-size: 0.95rem; margin-bottom: 4px; }
        .en-delete-meta {
          display: flex; gap: 12px; flex-wrap: wrap;
          font-size: 0.78rem; color: #94a3b8;
        }
        .en-delete-meta span { display: inline-flex; align-items: center; gap: 4px; }
        .en-delete-warning {
          display: inline-flex; align-items: center; gap: 6px;
          padding: 8px 14px; background: #fef2f2; color: #dc2626;
          border-radius: 8px; font-size: 0.78rem; font-weight: 700; margin-bottom: 20px;
        }
        .en-delete-actions { display: flex; gap: 10px; }

        /* ========== ANIMATIONS ========== */
        .en-spin { animation: en-spin 1s linear infinite; display: inline-block; }
        @keyframes en-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

        /* ========== RESPONSIVE ========== */
        @media (max-width: 992px) {
          .en-detail-grid { grid-template-columns: 1fr; }
        }
        @media (max-width: 768px) {
          .en-header { padding: 20px; }
          .en-header-title { font-size: 1.2rem; }
          .en-pagination { flex-direction: column; }
          .en-stat-value { font-size: 1.15rem; }
          .en-stat-icon-wrapper { width: 36px; height: 36px; font-size: 16px; }
          .en-modal-body { padding: 16px; }
          .en-detail-hero { padding: 16px; }
          .en-detail-avatar { width: 52px; height: 52px; font-size: 20px; }
        }
      `}</style>
    </div>
  );
}